import { Select as BaseSelect } from '@base-ui/react/select';
import { Check, ChevronDown } from 'lucide-react';
import { cva, cx, type VariantProps } from '@/cva.config';

const selectTriggerVariants = cva({
  base: 'inline-flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-2 transition-colors hover:border-border-active focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
  defaultVariants: {
    font: 'mono',
    size: 'sm',
  },

  variants: {
    font: {
      mono: 'font-mono',
    },
    size: {
      md: 'h-8 text-sm lg:text-base',
      sm: 'h-7 text-sm lg:text-base',
      xs: 'h-6 text-xs lg:text-sm',
    },
  },
});

export interface SelectItem<T extends string = string> {
  label: string;
  value: T;
}

export interface SelectProps<T extends string = string>
  extends VariantProps<typeof selectTriggerVariants> {
  className?: string;
  disabled?: boolean;
  id?: string;
  items: SelectItem<T>[];
  onValueChange: (value: T) => void;
  placeholder?: string;
  value: T;
}

export const Select = <T extends string = string>({
  className,
  disabled,
  font,
  id,
  items,
  onValueChange,
  placeholder,
  size,
  value,
}: SelectProps<T>) => (
  <BaseSelect.Root
    disabled={disabled}
    items={items}
    onValueChange={(next) => onValueChange(next as T)}
    value={value}
  >
    <BaseSelect.Trigger
      className={cx(selectTriggerVariants({ font, size }), className)}
      id={id}
    >
      <BaseSelect.Value placeholder={placeholder} />
      <BaseSelect.Icon>
        <ChevronDown className="size-3" />
      </BaseSelect.Icon>
    </BaseSelect.Trigger>
    <BaseSelect.Portal>
      <BaseSelect.Positioner sideOffset={4}>
        <BaseSelect.Popup className="z-40 rounded-md border border-border bg-surface p-1 shadow-md">
          {items.map((item) => (
            <BaseSelect.Item
              className="flex cursor-default items-center gap-2 rounded-sm px-2 py-1 text-base data-highlighted:bg-accent-soft data-highlighted:text-accent"
              key={item.value}
              value={item.value}
            >
              <BaseSelect.ItemIndicator>
                <Check className="size-3" />
              </BaseSelect.ItemIndicator>
              <BaseSelect.ItemText>{item.label}</BaseSelect.ItemText>
            </BaseSelect.Item>
          ))}
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  </BaseSelect.Root>
);
